/**
 * Entry Point: @atlaskit/media-card/types
 */
import { type FocusEvent, type MouseEvent } from 'react';
import {
	type FileDetails,
	type MediaClient,
	type Identifier,
	type ImageResizeMode,
	type FileState,
} from '@atlaskit/media-client';
import type { MediaSettings } from '@atlaskit/media-client-react/media-parsed-settings';
import type { SsrMediaItem } from '@atlaskit/media-client/ssr-media-item';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import {
	type MediaFeatureFlags,
	type NumericalCardDimensions,
	type SSR,
} from '@atlaskit/media-common';
import { type CardAction } from './card/actions';
import { type MediaCardError, type MediaCardErrorPrimaryReason } from './errors';
import { type ViewerOptionsProps, type MediaViewerExtensions } from '@atlaskit/media-viewer';
import type { MediaFilePreviewErrorPrimaryReason } from '@atlaskit/media-file-preview/media-file-preview-error';

export type CardStatus =
	| 'uploading'
	| 'loading'
	| 'processing'
	| 'loading-preview'
	| 'complete'
	| 'error'
	| 'failed-processing';

export type FilePreviewStatus = {
	hasFilesize: boolean;
	isPreviewable: boolean;
	hasPreview: boolean;
	isSupportedByBrowser: boolean;
};

export type FileStateFlags = {
	wasStatusProcessing: boolean;
	wasStatusUploading: boolean;
};

export type CardAppearance = 'auto' | 'image' | 'square' | 'horizontal';

export declare type CardDimensionValue = number | string;

export type CardPreviewSource =
	| 'local'
	| 'remote'
	| 'ssr-server'
	| 'ssr-client'
	| 'ssr-data'
	| 'cache-local'
	| 'cache-remote'
	| 'cache-ssr-client'
	| 'cache-ssr-server'
	| 'external';

export interface CardPreview {
	dataURI: string;
	orientation?: number;
	dimensions?: CardDimensions;
	source: CardPreviewSource;
	srcSet?: string;
}

export const isSSRPreview = (preview: CardPreview): boolean =>
	isSSRClientPreview(preview) || isSSRServerPreview(preview) || isSSRDataPreview(preview);

export const isSSRServerPreview = (preview: CardPreview): boolean => {
	const ssrClientSources: CardPreviewSource[] = ['ssr-server', 'cache-ssr-server'];
	return ssrClientSources.includes(preview.source);
};

export const isSSRClientPreview = (preview: CardPreview): boolean => {
	const ssrClientSources: CardPreviewSource[] = ['ssr-client', 'cache-ssr-client'];
	return ssrClientSources.includes(preview.source);
};

export const isSSRDataPreview = (preview: CardPreview): boolean => preview.source === 'ssr-data';

export enum MediaCardCursor {
	Action = 'pointer',
	NotReady = 'wait',
}

export interface CardDimensions {
	width?: CardDimensionValue;
	height?: CardDimensionValue;
}

export type TitleBoxIcon = 'LockFilledIcon';

export interface CardEvent {
	event: MouseEvent<HTMLElement>;
	mediaItemDetails?: FileDetails;
}

export interface CardFocusEvent {
	event: FocusEvent<HTMLElement>;
	mediaItemDetails?: FileDetails;
}

export interface InlineCardEvent {
	event: MouseEvent<HTMLElement> | React.KeyboardEvent;
	mediaItemDetails?: FileDetails;
}

export interface SharedCardProps {
	// Overlay the media file.
	readonly disableOverlay?: boolean;
	// Resize the media to 'crop' | 'fit' | 'full-fit' | 'stretchy-fit'.
	readonly resizeMode?: ImageResizeMode;
	// Includes media features like caption, timestamp etc.
	readonly featureFlags?: MediaFeatureFlags;
	// Sets meida card appearance
	readonly appearance?: CardAppearance;
	// Custom media card dimension like width and height.
	readonly dimensions?: CardDimensions;
	// Original media card dimension like width and height.
	readonly originalDimensions?: NumericalCardDimensions;
	// Array of additional media card actions.
	readonly actions?: Array<CardAction>;
	// Enable media card selectable.
	readonly selectable?: boolean;
	// Renders media card as selected, if selected is true.
	readonly selected?: boolean;
	// Alternate text for an media card.
	readonly alt?: string;
	// ID for testing the media card.
	readonly testId?: string;
	// Sets the title box background color.
	readonly titleBoxBgColor?: string;
	// Overrides the background color of the rendered media element. Applied as an inline style
	// on the underlying <img> for both SVG and raster media. For SVGs, providing this value also
	// disables the default white background that is otherwise used to mask transparency.
	readonly backgroundColor?: React.CSSProperties['backgroundColor'];
	// Sets the title box icon.
	readonly titleBoxIcon?: TitleBoxIcon;
	// Indicates the media card is being generated by AI (e.g. Rovo image-create).
	// When true, the upload state shows an animated rainbow border instead of a progress bar.
	readonly isAIGenerating?: boolean;
	// Marks the card as part of the CWR (create-with-Rovo) infographics flow, which uses a
	// distinct AI-generating loading treatment.
	readonly isCWR?: boolean;
	// Callback fired when the media card's image preview has rendered (i.e. the <img> onload event).
	// Receives the file identifier ID.
	readonly onPreviewRender?: (fileId: string) => void;
	// Sets viewer options
	readonly viewerOptions?: ViewerOptionsProps;
	/** Sets options for viewer **/
	readonly includeHashForDuplicateFiles?: boolean;
}

export interface CardOnClickCallback {
	(result: CardEvent, analyticsEvent?: UIAnalyticsEvent): void;
}

export interface InlineCardOnClickCallback {
	(result: InlineCardEvent, analyticsEvent?: UIAnalyticsEvent): void;
}

export interface CardEventProps {
	// Callback function to be called when user clicks on media card.
	readonly onClick?: CardOnClickCallback;
	// Callback function to be called when the mouse pointer is moved onto an media card.
	readonly onMouseEnter?: (result: CardEvent) => void;
	// Callback function to be called when keyboard focus moves onto a media card from outside of it.
	readonly onFocus?: (result: CardFocusEvent) => void;
	/** Callback function to be called when video enters and exit fullscreen.
	 * `fullscreen = true` indicates video enters fullscreen
	 * `fullscreen = false` indicates video exits fullscreen
	 */
	readonly onFullscreenChange?: (fullscreen: boolean) => void;

	// Callback function to be called when video controls panel rendered.
	readonly videoControlsWrapperRef?: React.Ref<HTMLDivElement>;
}

export interface CardProps extends SharedCardProps, CardEventProps {
	/** Instance of MediaClient. */
	readonly mediaClient: MediaClient;
	/** Instance of file identifier. */
	readonly identifier: Identifier;
	/** Lazy loads the media file. */
	readonly isLazy?: boolean;
	/** Uses the inline player for media file. */
	readonly useInlinePlayer?: boolean;
	/** Uses media MediaViewer to preview the media file. */
	readonly shouldOpenMediaViewer?: boolean;
	/** Media file list to display in Media Viewer. */
	readonly mediaViewerItems?: Identifier[];
	/** Retrieve auth based on a given context. */
	readonly contextId?: string;
	/** Enables the download button for media file. */
	readonly shouldEnableDownloadButton?: boolean;
	/** Server-Side-Rendering modes are "server" and "client" */
	readonly ssr?: SSR;
	/** Disable tooltip for the card */
	readonly shouldHideTooltip?: boolean;
	/** General Media Settings */
	readonly mediaSettings?: MediaSettings;
	/**
	 * Pre-hydrated SSR metadata from a Relay fragment.
	 * When provided and `fg('platform_media_ssr_data_seed')` is on, the card seeds
	 * `useFileState` with this data and skips the `items()` API call for files
	 * whose `processingStatus` is `succeeded`.
	 * @see https://product-fabric.atlassian.net/browse/BMPT-7914
	 */
	readonly ssrFileState?: FileState;
	/**
	 * Raw SSR media item from a host payload (e.g. Confluence recorded media nodes).
	 * Used when `ssrFileState` is not provided. Converted to FileState internally
	 * when `fg('platform_media_ssr_data_seed')` is on.
	 */
	readonly ssrMediaItem?: SsrMediaItem;
	/** General Error handling include status errors and display errors*/
	readonly onError?: (
		reason: MediaFilePreviewErrorPrimaryReason | MediaCardErrorPrimaryReason,
	) => void;
	/** Extensions for the media viewer (e.g. comment button in header). */
	readonly mediaViewerExtensions?: MediaViewerExtensions;
	/**
	 * Optional fallback fetcher to retrieve the media filename from another service.
	 * Workaround for #hot-301450 where media service is missing filenames for DC -> Cloud migrated media.
	 * Receives the file ID and should resolve to the filename string.
	 */
	readonly fallbackMediaNameFetcher?: (id: string) => Promise<string>;
}

export interface CardState {
	status: CardStatus;
	isCardVisible: boolean;
	shouldAutoplay?: boolean;
	isPlayingFile: boolean;
	mediaViewerSelectedItem?: Identifier;
	fileState?: FileState;
	progress?: number;
	cardPreview?: CardPreview;
	error?: MediaCardError;
	cardRef: HTMLDivElement | null;
	isBannedLocalPreview: boolean;
	previewDidRender: boolean;
	wasResolvedUpfrontPreview: boolean;
	shouldUpdateStateForIdentifier?: boolean;
}
